import { useState, MouseEvent, FC } from 'react';
import { Draggable } from 'react-beautiful-dnd';

import classNames from 'classnames';
import { Plus, Trash2, Pencil, ChevronDown } from 'lucide-react';
import { BaseMenu, BaseMenuItem } from 'wbs-components-lib';

import { Accordion, AccordionSummary, Tooltip, Typography, Box, AccordionDetails } from '@mui/material';

import { textStyles } from '@shared/styles';

import { VipTaskElementProps } from './types';
import { VipTaskItems } from './VipTaskItems';

import styles from './VipTaskElement.module.css';

const { defaultText } = textStyles;

const { vipTaskItem, actionsContainer, vipTaskItemDraggable } = styles;

export const VipTaskElement: FC<VipTaskElementProps> = ({
  vipTask,
  handleVipTaskItemsExpand,
  expandedVipTaskItemsId,
  index,
  handleVipTaskEditModalOpen,
  handleVipTaskDeleteModalOpen,
  handleVipTaskItemEditModalOpen,
  handleVipTaskItemDeleteModalOpen,
  handleVipTaskItemCreateModalOpen,
  handleVipTaskItemExport,
  handleVipTaskItemValidateModalOpen,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const isMenuOpened = Boolean(menuAnchor);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const { id: vipTaskId, title, type, marks } = vipTask;

  const menuItems: BaseMenuItem[] = [
    {
      icon: <Pencil />,
      text: 'Edit',
      onClick: () => handleVipTaskEditModalOpen(vipTask),
    },
    {
      icon: <Plus />,
      text: 'Create task item',
      onClick: () => handleVipTaskItemCreateModalOpen(vipTask),
    },
    {
      icon: <Trash2 />,
      text: 'Delete',
      onClick: () => handleVipTaskDeleteModalOpen(vipTask),
    },
  ];

  const handleItemsExpand = () => {
    const currentVipTaskItemsIdExpanded = isItemsExpanded ? '' : vipTaskId;

    handleVipTaskItemsExpand(currentVipTaskItemsIdExpanded);
  };

  const isItemsExpanded = expandedVipTaskItemsId === vipTaskId;
  const expandSlotsTitle = `${isItemsExpanded ? 'Collapse' : 'Expand'} task items`;

  return (
    <Draggable key={vipTaskId} draggableId={vipTaskId} index={index}>
      {(provided, snapshot) => (
        <Accordion
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
          expanded={isItemsExpanded}
          className={classNames({
            [vipTaskItemDraggable]: snapshot.isDragging,
          })}
        >
          <AccordionSummary
            expandIcon={
              <Tooltip title={expandSlotsTitle}>
                <ChevronDown onClick={handleItemsExpand} />
              </Tooltip>
            }
            className={vipTaskItem}
          >
            <Tooltip title={title} placement="top-start">
              <Typography className={defaultText}>Name: {title}</Typography>
            </Tooltip>
            <Tooltip title={title} placement="top-start">
              <Typography className={defaultText}>Type: {type}</Typography>
            </Tooltip>
            <Tooltip title={title} placement="top-start">
              <Typography className={defaultText}>Marks: {marks.join(',')}</Typography>
            </Tooltip>
            <Box className={actionsContainer}>
              <Typography marginLeft="10px" className={defaultText}>
                Settings:
              </Typography>
              <BaseMenu
                menuItems={menuItems}
                menuAnchor={menuAnchor}
                isMenuOpened={isMenuOpened}
                handleOpenMenu={handleOpenMenu}
                handleCloseMenu={handleCloseMenu}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <VipTaskItems
              vipTask={vipTask}
              isItemsExpanded={isItemsExpanded}
              handleVipTaskItemEditModalOpen={handleVipTaskItemEditModalOpen}
              handleVipTaskItemDeleteModalOpen={handleVipTaskItemDeleteModalOpen}
              handleVipTaskItemExport={handleVipTaskItemExport}
              handleVipTaskItemValidateModalOpen={handleVipTaskItemValidateModalOpen}
            />
          </AccordionDetails>
        </Accordion>
      )}
    </Draggable>
  );
};
